/**
 * The NOW chip's model (1.O-1 deliverable 16), pure: what the chip reads, and the `clock.advance`
 * a typed phrase sends. "Advance by" reads its text as `now + <text>`; "Set NOW" takes a point or
 * "now". The first NOW (no clock row yet) is `reason: 'bootstrap'` at `baseRv: 0`, as the
 * contract's `EXAMPLE_CLOCK_FIRST_SET`; every later set is `manual`.
 */
import { add, type CompiledCalendar, formatTick, parse } from '@codex/calendar';
import type { ClockAdvanceRequestV1, ClockAdvanceV1, ClockV1 } from '@codex/contracts';
import { readWhen, toPlacementDates } from './when-model.js';
import {
  ADVANCE_NEEDS_A_GAP,
  NOW_NEEDS_A_DATE,
  NOW_UNAVAILABLE,
  NOW_UNREAD,
  reasonText,
  SET_NOW,
  SET_NOW_FIRST,
  wordAt,
} from './words.js';

/** The reasons "Advance by" offers, in the menu's order. */
export const ADVANCE_REASONS = ['scene', 'short_rest', 'long_rest', 'travel', 'downtime'] as const;
export type AdvanceReason = (typeof ADVANCE_REASONS)[number];

export const REASON_WORDS: Readonly<Record<AdvanceReason, string>> = {
  scene: 'Scene',
  short_rest: 'Short rest',
  long_rest: 'Long rest',
  travel: 'Travel',
  downtime: 'Downtime',
};

export interface ClockCtx {
  readonly calendar: CompiledCalendar;
  /** The present tick; null while NOW is unset or unknown. */
  readonly now: number | null;
  /** The clock row; null before the first set (`clock.get` answered null) or when unread. */
  readonly clock: Pick<ClockV1, 'rv'> | null;
  /**
   * `unset` only once `clock.get` has answered (null, or a row without NOW); `unknown` when it
   * failed otherwise, and nothing is sent then: a bootstrap could meet a clock it never read.
   */
  readonly nowState: 'set' | 'unset' | 'unknown';
}

export type ClockInput =
  | { readonly kind: 'delta'; readonly text: string; readonly reason: AdvanceReason }
  | { readonly kind: 'set'; readonly text: string };

export type ClockPlan =
  | {
      readonly ok: true;
      readonly request: ClockAdvanceRequestV1;
      /** NOW after the advance, when it can be worked out here. */
      readonly tick: number | null;
    }
  | { readonly ok: false; readonly reason: string };

const refuse = (reason: string): ClockPlan => ({ ok: false, reason });

/** A typed advance or set as the `clock.advance` it sends, or the reason it cannot. */
export function clockPlan(input: ClockInput, ctx: ClockCtx): ClockPlan {
  const text = input.text.trim();
  if (ctx.nowState === 'unknown') return refuse(NOW_UNREAD);
  if (input.kind === 'delta') {
    if (ctx.clock === null || ctx.now === null) return refuse(SET_NOW_FIRST);
    if (text === '') return refuse(ADVANCE_NEEDS_A_GAP);
    const phrase = `now + ${text}`;
    const parsed = parse(phrase, { calendar: ctx.calendar, now: ctx.now });
    if (parsed.kind === 'invalid') {
      return refuse(
        parsed.reason === 'unexpected_text' || parsed.reason === 'empty'
          ? ADVANCE_NEEDS_A_GAP
          : reasonText(parsed.reason, wordAt(phrase, parsed.at)),
      );
    }
    if (parsed.kind !== 'now' || parsed.offset === null) return refuse(ADVANCE_NEEDS_A_GAP);
    const { n, unit } = parsed.offset;
    const moved = add(ctx.calendar, ctx.now, n, unit);
    if (!moved.ok) return refuse(reasonText(moved.reason));
    return {
      ok: true,
      request: {
        kind: 'delta',
        deltaN: n,
        deltaUnit: unit,
        reason: input.reason,
        baseRv: ctx.clock.rv,
      },
      tick: moved.tick,
    };
  }
  const read = readWhen(text, [], { calendar: ctx.calendar, now: ctx.now });
  if (read.parsed.kind === 'invalid' || read.preview.status === 'invalid') {
    const reason =
      read.parsed.kind === 'invalid' ? read.parsed.reason : (read.preview.reason ?? 'empty');
    const at = read.parsed.kind === 'invalid' ? read.parsed.at : 0;
    return refuse(reasonText(reason, wordAt(text, at)));
  }
  // NOW is one moment: a point or "now", never a range ("1040 – 1045"), an open side ("since
  // 1040"), a relative phrase or "undated".
  const twoSided = read.parsed.kind === 'point' && read.parsed.hiParts !== null;
  if ((read.kind !== 'point' && read.kind !== 'now') || twoSided) return refuse(NOW_NEEDS_A_DATE);
  const dates = toPlacementDates(read.parsed, {
    lasts: false,
    rawInput: text,
    calendar: ctx.calendar,
  });
  if (dates?.start == null) return refuse(NOW_NEEDS_A_DATE);
  const first = ctx.clock === null;
  return {
    ok: true,
    request: {
      kind: 'set',
      set: dates.start,
      reason: first ? 'bootstrap' : 'manual',
      baseRv: first ? 0 : (ctx.clock?.rv ?? 0),
    },
    tick: read.preview.earliest,
  };
}

/** A tick as the chip shows it: day and time. */
export function nowText(calendar: CompiledCalendar, tick: number): string {
  return formatTick(calendar, tick, 'minute') ?? `${tick}`;
}

export interface ChipState {
  readonly calendar: CompiledCalendar | null;
  readonly now: number | null;
  readonly nowState: 'set' | 'unset' | 'unknown';
}

/** The chip's text: NOW to the minute, "Set NOW" while unset; null with no calendar. */
export function chipText(state: ChipState): string | null {
  if (state.calendar === null) return null;
  if (state.nowState === 'set' && state.now !== null) return nowText(state.calendar, state.now);
  return state.nowState === 'unset' ? SET_NOW : NOW_UNAVAILABLE;
}

/** The advance "Undo last advance" undoes: the newest one not undone yet. */
export function lastAdvance(clock: Pick<ClockV1, 'advances'> | null): ClockAdvanceV1 | null {
  return clock?.advances.find((a) => a.undoneAt === null) ?? null;
}
